import React, { useMemo } from 'react';
import styled from 'styled-components';

import DatePicker from 'antd/es/date-picker';

import { datePickerFormats } from 'config/common';

import useDatePicker from 'hooks/useDatePicker';
import useSearcher from 'hooks/useSearcher';
import useQueryParam from 'hooks/useQueryParam';

import useStats from 'hooks/useStats';
import { StatType } from 'services/stats';

import MainLayout from 'layouts/Main.layout';

import { Row, Col, Space } from 'components/layout';
import { Text, Link } from 'components/Text';
import PageHeader from 'components/MainPageHeader';
import Table from 'components/Table';
import FilterBar from 'components/FilterBar';
import Search from 'components/Search';
import PeriodFilter from 'components/PeriodFilter';

import { localToUtcTs } from 'helpers/date';

import { CARTS_STATS_PERIODS } from '../constants';

const CartsStatsPageHeader = styled(PageHeader)`
  & .ant-page-header-heading-sub-title {
    display: inline-flex;
    justify-content: center;
    flex: 1;
    margin-right: 110px;
  }
`;

const CartsStatsPage = () => {
  const [period, setPeriod] = useQueryParam('period', 'live');
  const datePicker = useDatePicker(period, { queryParam: 'dt' });
  const [searchVal, setSearchVal] = useQueryParam('s', '');
  const searcher = useSearcher([
    'name',
    'door_name',
  ]);

  const statsParams = useMemo(() => {
    if (period === 'live') {
      return {};
    }
    const mode = datePicker.mode;
    const date = datePicker.date;
    const fromTs = localToUtcTs(date.clone().startOf(mode === 'date' ? 'day' : mode).toDate(), true);
    const toTs = localToUtcTs(date.clone().endOf(mode === 'date' ? 'day' : mode).toDate(), true);
    return {
      from: fromTs,
      to: toTs,
    };
  }, [period, datePicker.date, datePicker.mode]);

  const { state: { isLoading, stats } } = useStats(StatType.cartStats, statsParams, {
    autoFetch: true,
    reset: true,
  });

  const lastSeen = useMemo(() => {
    const { last_seen: data = [] } = stats;
    searcher.setData(data);
    return searcher.search(searchVal);
  }, [searcher, stats, searchVal])

  // const summary = useMemo(() => {
  //   const { stats: summaryStats } = stats;
  //   return summaryStats ? summaryStats[0] || {} : {};
  // }, [stats]);

  // const flowParams = useMemo(() => {
  //   return {};
  // }, []);

  // const { state: { stats: cartsFlow, isLoading: cartsFlowLoading } } = useStats(StatType.cartFlow, flowParams, {
  //   autoFetch: true,
  //   reset: true,
  // });

  return (
    <MainLayout>
      <CartsStatsPageHeader
        title="Carts Stats"
        subTitle={
          <PeriodFilter
            defaultActiveKey={period}
            onChange={setPeriod}
            options={CARTS_STATS_PERIODS}
          />
        }
        icon="shopping-cart"
      />

      {period !== 'live' && (
        <Row style={{ padding: '12px 24px' }}>
          <Space size="large">
            <DatePicker
              mode={datePicker.mode}
              picker={datePicker.mode}
              value={datePicker.date}
              format={datePickerFormats[period]}
              onChange={date => datePicker.setDate(date)}
              style={{ width: '260px' }}
              allowClear={false}
            />
          </Space>
        </Row>
      )}

      <Row style={{ padding: '24px' }}>
        <Col span={24}>
          <FilterBar
            title="All Carts"
            style={{ width: '100%', paddingBottom: '12px' }}
            size="large"
          >
            <FilterBar.Slot align="left">
              <Search
                placeholder="Search cart, door"
                value={searchVal}
                onChange={setSearchVal}
              />
            </FilterBar.Slot>
          </FilterBar>
          <Table
            dataSource={lastSeen}
            loading={isLoading}
            rowKey={row => row.id}
          >
            <Table.Column type="link" title="Cart Code" dataIndex="name" to={({ item }) => `/carts/${item.device_id}`} />
            <Table.Column type="text" title="Current Location" align="center" render={item => {
              if (item.door_id) {
                return (
                  <Link to={`/doors/${item.door_id}`}>
                    {item.door_name}
                  </Link>
                );
              }
              return (
                <Text
                  color={({ 'Empty': '#F5222D', 'In transit': '#FA8C16' })[item.cart_status]}
                >
                  {item.cart_status}
                </Text>
              );
            }} />
            <Table.Column type="duration" title="Avg. time in container" dataIndex="avg_container_mins" align="right" inMins={true} withSecs={false} />
            <Table.Column type="duration" title="Avg. time in trailer" dataIndex="avg_trailer_mins" align="right" inMins={true} withSecs={false} />
            <Table.Column type="duration" title="Avg. time in transit" dataIndex="avg_area_mins" align="right" inMins={true} withSecs={false} />
            <Table.Column type="duration" title="Avg. time empty" dataIndex="avg_empty_mins" align="right" inMins={true} withSecs={false} />
          </Table>
        </Col>
      </Row>
    </MainLayout >
  );
};

export default CartsStatsPage;
